import { CopyOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import { FC, ReactNode, useEffect, useState } from "react";
import {
    getMarketInfo,
    getMarketTypesList,
    getNetworkInfo,
} from "tm-shared/api";
import { Market, UserRole } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { PrimaryText } from "@/components/Typography/Styled";
import { BlockLoader } from "@/components/UI/BlockLoader";
import { ROUTES } from "@/constants";
import { useAppSelector } from "@/hooks/redux";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import { StyledAPTable, StyledAPTableLink, StyledAPTableRow } from "../Styled";

const BANNED_ROLES_FOR_PLAN: UserRole[] = [UserRole.Svm, UserRole.Md];

export const getDayWord = (num: number): string => {
    const absNum = Math.abs(num);
    switch (true) {
        case absNum % 10 === 1 && absNum % 100 !== 11:
            return `${num} день`;
        case [2, 3, 4].includes(absNum % 10) &&
            ![12, 13, 14].includes(absNum % 100):
            return `${num} дні`;
        default:
            return `${num} днів`;
    }
};

export interface KeyValueItem {
    label: ReactNode;
    value: ReactNode;
    copyValue?: string | object;
}

interface InfoTabProps {
    market: Market;
}

export const InfoTab: FC<InfoTabProps> = ({ market }) => {
    const { offlineMode } = useAppSelector((s) => s.offline);
    const { role } = useAppSelector((state) => state.user);

    const [network, setNetwork] = useState<{ name: string; id: string } | null>(
        null,
    );
    const [typesList, setTypesList] = useState<Record<string, string> | null>(
        null,
    );
    const [parent, setParent] = useState<Market | null>(null);
    const [loading, setLoading] = useState(true);

    const {
        category,
        type,
        id,
        context,
        contragent,
        externalMarketId,
        title,
        geo,
        storage,
        partner,
        networkId,
        parentInfo,
    } = market;

    useEffect(() => {
        if (offlineMode) {
            setLoading(false);
            return;
        }

        const getInfo = async () => {
            setLoading(true);

            const promises = [
                getMarketTypesList().then(({ result }) => setTypesList(result)),
            ];

            if (networkId) {
                promises.push(
                    getNetworkInfo({ network: networkId }).then(({ result }) =>
                        setNetwork(result),
                    ),
                );
            }

            if (parentInfo.parentId) {
                promises.push(
                    getMarketInfo({
                        marketId: parentInfo.parentId,
                    }).then(({ result }) => setParent(result)),
                );
            }

            await Promise.allSettled(promises);
            setLoading(false);
        };

        getInfo();
    }, []);

    const shelvesLength = context?.shelfSize?.total || 0;
    const shelvesLengthMeters = (shelvesLength / 100).toFixed(1);

    const items = [
        { label: "ID маркету", value: id, copyValue: id },

        externalMarketId && {
            label: "ID в обліковому записі партнера",
            value: externalMarketId,
            copyValue: externalMarketId,
        },

        { label: "Категорія ТТ", value: category },
        { label: "Тип ТТ", value: typesList?.[type] ?? type },

        {
            label: "Загальна довжина полиць",
            value: `${shelvesLengthMeters}м (${shelvesLength}см)`,
        },
        {
            label: "Термін поставки",
            value: getDayWord(context?.deliveryDayInterval || 0),
        },
        {
            label: "Термін відстрочки оплати по Формі 1",
            value: getDayWord(context?.paymentF1DayInterval || 0),
        },
        {
            label: "Термін відстрочки оплати по Формі 2",
            value: getDayWord(context?.paymentF2DayInterval || 0),
        },

        { label: "Партнер", value: partner.name, copyValue: partner },

        title && { label: "Додаткова назва", value: title },

        geo && {
            label: "Координати",
            value: (
                <a
                    href={`https://www.google.com/maps/place/${geo}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textAlign: "right" }}
                >
                    {geo.split(",").join(", ")}
                </a>
            ),
        },

        contragent?.id && {
            label: "ІНН/ЄДРПОУ",
            value: contragent.id,
            copyValue: contragent,
        },

        storage && { label: "Склад", value: storage.name, copyValue: storage },

        storage?.storageCity && {
            label: "Місто складу",
            value: storage.storageCity.name,
            copyValue: storage.storageCity,
        },

        parent && {
            label: "Батьківська торгова точка",
            value: (
                <StyledAPTableLink
                    to={buildRoutePath(ROUTES.MarketDetails, {
                        marketId: parent.id,
                    })}
                >
                    {parent.name}
                </StyledAPTableLink>
            ),
        },

        network && {
            label: "Національна мережа",
            value: network.name,
            copyValue: network,
        },
    ].filter(Boolean) as KeyValueItem[];

    const links = [
        { to: "contracts", text: "Контракти" },
        { to: "shelves", text: "Конфігурація полиць" },
    ];

    if (!BANNED_ROLES_FOR_PLAN.includes(role)) {
        links.push({ to: "plan", text: "План продажів" });
    }

    if (loading) {
        return <BlockLoader />;
    }

    return (
        <StyledAPTable>
            {items.map(({ label, value, copyValue }, index) => (
                <StyledAPTableRow key={`${String(label)}-${index}`}>
                    <PrimaryText>{label}</PrimaryText>

                    <Flex gap={4} align="center" justify="end">
                        {typeof value === "string" ? (
                            <PrimaryText fontWeight="300" textAlign="right">
                                {value}
                            </PrimaryText>
                        ) : (
                            value
                        )}

                        {copyValue && (
                            <Button
                                icon={<CopyOutlined />}
                                type="text"
                                onClick={() => copyObjectToClipboard(copyValue)}
                            />
                        )}
                    </Flex>
                </StyledAPTableRow>
            ))}

            {links.map(({ to, text }) => (
                <StyledAPTableRow key={to}>
                    <StyledAPTableLink to={to}>{text}</StyledAPTableLink>
                </StyledAPTableRow>
            ))}
        </StyledAPTable>
    );
};
